import AppLayout from '@/layout/AppLayout.vue'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useSupabase } from '@/composables/use-supabase'
import { useSuperAdmin } from '@/composables/use-super-admin'
import { useOrganizationsStore } from '@/stores/organizations'
import { clearChunkReloadMarker, shouldReloadForDynamicImportError } from './chunk-reload'
import { shouldRedirectSuperAdminToOrganizations } from './super-admin-landing'

// The org child routes are identical for the regular (/org/:orgSlug) and
// super-admin (/super-admin/org/:orgSlug) trees — only the route name suffix
// and the parent's meta differ. Define the children once and stamp out both.
const orgChildRoutes: ReadonlyArray<{ path: string; suffix: string; component: () => Promise<unknown> }> = [
  { path: '', suffix: 'Dashboard', component: () => import('@/views/Dashboard.vue') },
  { path: 'members', suffix: 'Members', component: () => import('@/views/Members.vue') },
  { path: 'subscription', suffix: 'Subscription', component: () => import('@/views/Subscription.vue') },
  { path: 'branding', suffix: 'Branding', component: () => import('@/views/Branding.vue') },
  { path: 'member-experience', suffix: 'MemberExperience', component: () => import('@/views/MemberExperience.vue') },
  { path: 'integrations', suffix: 'Integrations', component: () => import('@/views/Integrations.vue') },
  { path: 'company-metrics', suffix: 'CompanyMetrics', component: () => import('@/views/CompanyMetrics.vue') },
  { path: 'notes', suffix: 'Notes', component: () => import('@/views/Notes.vue') },
]

const buildOrgTree = (
  path: string,
  namePrefix: string,
  meta: RouteRecordRaw['meta'],
  extraChildren: ReadonlyArray<{ path: string; suffix: string; component: () => Promise<unknown> }> = [],
): RouteRecordRaw => ({
  path,
  component: AppLayout,
  meta,
  children: [...orgChildRoutes, ...extraChildren].map(child => ({
    path: child.path,
    name: `${namePrefix}${child.suffix}`,
    component: child.component,
  })),
})

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      component: AppLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'orgSelector',
          component: () => import('@/views/OrganizationSelector.vue')
        },
        {
          path: '/account',
          name: 'account',
          component: () => import('@/views/Account.vue')
        },
        {
          path: '/super-admin',
          name: 'superAdmin',
          component: () => import('@/views/SuperAdmin.vue'),
          meta: { requiresSuperAdmin: true }
        },
        {
          path: '/super-admin/user-subscriptions',
          name: 'superAdminUserSubscriptions',
          component: () => import('@/views/UserSubscriptions.vue'),
          meta: { requiresSuperAdmin: true }
        },
        {
          path: '/super-admin/member-activity',
          name: 'superAdminMemberActivity',
          component: () => import('@/views/MemberActivity.vue'),
          meta: { requiresSuperAdmin: true }
        },
        {
          path: '/super-admin/supplement-signups',
          name: 'superAdminSupplementSignups',
          component: () => import('@/views/SupplementSignups.vue'),
          meta: { requiresSuperAdmin: true }
        },
        {
          path: '/super-admin/send-email',
          name: 'superAdminSendEmail',
          component: () => import('@/views/SendEmail.vue'),
          meta: { requiresSuperAdmin: true }
        },
        {
          path: '/super-admin/notes',
          name: 'superAdminNotes',
          component: () => import('@/views/Notes.vue'),
          meta: { requiresSuperAdmin: true }
        },
        {
          path: '/super-admin/coach-earnings',
          name: 'superAdminCoachEarnings',
          component: () => import('@/views/CoachEarnings.vue'),
          meta: { requiresSuperAdmin: true }
        },
        {
          path: '/super-admin/supplement-catalog',
          name: 'superAdminSupplementCatalog',
          component: () => import('@/views/SupplementCatalog.vue'),
          meta: { requiresSuperAdmin: true }
        },
      ]
    },
    buildOrgTree('/org/:orgSlug', 'org', { requiresAuth: true, requiresOrg: true }),
    buildOrgTree('/super-admin/org/:orgSlug', 'superAdminOrg', { requiresAuth: true, requiresSuperAdmin: true }),
    {
      path: '/auth/login',
      name: 'login',
      component: () => import('@/views/pages/auth/Login.vue')
    },
    {
      path: '/auth/access',
      name: 'accessDenied',
      component: () => import('@/views/pages/auth/Access.vue')
    },
    {
      path: '/auth/error',
      name: 'error',
      component: () => import('@/views/pages/auth/Error.vue')
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'notfound',
      component: () => import('@/views/pages/NotFound.vue')
    },
  ]
})

router.beforeEach(async (to) => {
  const publicRoutes = ['login', 'accessDenied', 'error', 'notfound']
  if (publicRoutes.includes(to.name as string)) {
    return true
  }

  const { supabase } = useSupabase()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session) {
    return { name: 'login' }
  }

  // The caller context (super-admin flag + owner/admin orgs) is read once per
  // session from GET /admin/me and reused on every later navigation.
  const orgStore = useOrganizationsStore()
  try {
    await orgStore.loadCallerContext(session.user.id)
  } catch (error) {
    console.error('Error loading caller context:', error)
    return { name: 'accessDenied' }
  }

  const { isSuperAdmin } = useSuperAdmin()
  if (!isSuperAdmin.value && orgStore.organizations.length === 0) {
    return { name: 'accessDenied' }
  }

  // Super admins land on Organizations instead of the member-facing org selector.
  if (shouldRedirectSuperAdminToOrganizations(to.name as string | undefined, isSuperAdmin.value)) {
    return { name: 'superAdmin' }
  }

  // Guard super admin routes
  if (to.meta.requiresSuperAdmin) {
    if (!isSuperAdmin.value) {
      return { name: 'orgSelector' }
    }

    // Resolve org from slug for SA org routes. The list is re-read only when
    // it lacks the slug (first visit, or an org created or renamed since).
    const orgSlug = to.params.orgSlug as string | undefined
    if (orgSlug) {
      const findOrg = () => orgStore.allOrganizations.find((o) => o.slug === orgSlug)
      if (!findOrg()) await orgStore.getAllOrganizations()
      const org = findOrg()
      if (!org) {
        return { name: 'superAdmin' }
      }
      orgStore.setActiveOrganization(org)
    }

    return true
  }

  // Guard org-scoped routes: validate user has admin/owner in this specific org
  if (to.meta.requiresOrg) {
    const orgSlug = to.params.orgSlug as string

    // A slug the cached context lacks may be an org granted or renamed since
    // the session started: re-read the context once before refusing.
    if (!orgStore.resolveOrgBySlug(orgSlug)) {
      await orgStore
        .loadCallerContext(session.user.id, { refresh: true })
        .catch((error) => console.error('Error refreshing caller context:', error))
    }
    const org = orgStore.resolveOrgBySlug(orgSlug)
    if (!org) {
      // User doesn't have admin/owner access to this org
      return { name: 'orgSelector' }
    }

    orgStore.setActiveOrganization(org)
    return true
  }

  return true
})

// A new deploy replaces hashed route-chunk filenames; a stale client then fails
// to lazily import the old chunk. Reload to the target route to pull fresh assets.
router.onError((error, to) => {
  if (shouldReloadForDynamicImportError(error, to.fullPath)) {
    window.location.assign(to.fullPath)
  }
})

router.afterEach((to) => {
  clearChunkReloadMarker(to.fullPath)
})

export default router
